if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      isRestoringSelections = false;

      constructor() {
        super();
        this._defaultsCleared = false;
        this.quantityInput = this.querySelector('.quantity__input');
        this.klaviyoWaitlist = this.querySelector('klaviyo-waitlist');
      }

      connectedCallback() {
         if (!this._defaultsCleared) {
          this.clearInitialSelection();
          this._defaultsCleared = true;
        }
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        this.initSizeHoverMessages();
        this.restorePersistentOptions();
        this.initWaitlistSizeSelection();
        // Initialize button state for single-variant products (they don't trigger variant changes)
        this.initButtonState();
      }

      initWaitlistSizeSelection() {
        // Listen for size button clicks to activate waitlist button
        const sizeInputs = this.querySelectorAll('.js-product-swatch');
        if (!sizeInputs || !sizeInputs.length) return;

        sizeInputs.forEach((input) => {
          input.addEventListener('change', () => {
            if (input.checked && input.classList.contains('waitlist')) {
              // Notify waitlist component that a waitlistable size was selected
              if (this.klaviyoWaitlist && typeof this.klaviyoWaitlist.onSizeSelected === 'function') {
                this.klaviyoWaitlist.onSizeSelected();
              }
            }
          });
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.capturePersistentOptions();

        const optionGroups = this.querySelectorAll('variant-selects .product-form__input');

        // Count unique option groups by name to handle duplicate groups during viewTransition
        const uniqueGroupNames = new Set(
          Array.from(optionGroups).map(group => group.querySelector('legend')?.textContent?.trim()).filter(Boolean)
        );
        const totalGroups = uniqueGroupNames.size;

        const allSelected = selectedOptionValues.length === totalGroups;

        if (!allSelected) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: (html) => {
            if (shouldSwapProduct) {
              this.handleSwapProduct(productUrl, shouldFetchFullPage)(html);
            } else {
              this.handleUpdateProductInfo(productUrl)(html);
            }
            this.initSizeHoverMessages();
          },
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        if (!productForm) return;

        const submitButton = productForm.querySelector('.product-form__submit');
        const canToggleSubmit = typeof productForm.toggleSubmitButton === 'function';

        if (submitButton && submitButton.hasAttribute('disabled') && canToggleSubmit) {
          productForm.toggleSubmitButton(true);
        }

        if (typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage();
        }
        if (typeof productForm.hideSelectSizeNotice === 'function') {
          productForm.hideSelectSizeNotice();
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        if (this.abortController) {
          if (!this.abortController.signal.aborted) {
            this.abortController.abort('renderProductInfo superseded');
          }
          this.abortController = null;
        }
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');

        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          // Re-initialize size selection listeners after variant-selects is replaced
          this.initWaitlistSizeSelection();
        }
      }

      capturePersistentOptions() {
        if (this.isRestoringSelections) return;

        const variantSelects = this.variantSelectors;
        if (!variantSelects) return;

        const store = this.getPersistedOptionStore();
        const normalize = (value) => value?.toLowerCase().trim();

        variantSelects
          .querySelectorAll('input[type="radio"][data-option-value-id][data-option-name]:checked')
          .forEach((input) => {
            const { optionValueId, optionName } = input.dataset;
            if (!optionName) return;
            const normalizedName = normalize(optionName);
            if (!normalizedName) return;

            const selectionValue = input.value;

            store[normalizedName] = {
              optionValueId: optionValueId || null,
              value: selectionValue,
              label: selectionValue,
            };
          });

        variantSelects.querySelectorAll('select[data-option-name]').forEach((select) => {
          const optionName = select.dataset.optionName;
          if (!optionName) return;

          const normalizedName = normalize(optionName);
          if (!normalizedName) return;

          const selectedOption = select.selectedOptions?.[0];
          if (!selectedOption) return;

          const optionValueId = selectedOption.dataset.optionValueId || null;
          const optionValue = selectedOption.value;
          const optionLabel = selectedOption.textContent?.trim() || optionValue;

          store[normalizedName] = {
            optionValueId,
            value: optionValue,
            label: optionLabel,
          };
        });
      }

      restorePersistentOptions() {
        const store = this.getPersistedOptionStore();
        const entries = Object.entries(store || {});
        if (!entries.length) return;

        window.requestAnimationFrame(() => {
          const variantSelects = this.variantSelectors;
          if (!variantSelects) return;

          const normalize = (value) => value?.toLowerCase().trim();

          entries.forEach(([normalizedName, data]) => {
            if (!normalizedName || !data) return;
            const { optionValueId, value, label } = data;

            const matchRadio = Array.from(
              variantSelects.querySelectorAll('input[type="radio"][data-option-name][data-option-value-id]')
            ).find((input) => {
              if (input.disabled) return false;
              if (normalize(input.dataset.optionName) !== normalizedName) return false;
              if (optionValueId && input.dataset.optionValueId === optionValueId) return true;
              const inputValue = normalize(input.value);
              if (value && inputValue === normalize(value)) return true;
              if (label && inputValue === normalize(label)) return true;
              return false;
            });

            if (matchRadio && !matchRadio.checked) {
              this.isRestoringSelections = true;
              try {
                matchRadio.checked = true;
                matchRadio.dispatchEvent(new Event('change', { bubbles: true }));
              } finally {
                this.isRestoringSelections = false;
              }
              return;
            }

            const selectMatch = Array.from(variantSelects.querySelectorAll('select[data-option-name]')).find(
              (select) => normalize(select.dataset.optionName) === normalizedName
            );

            if (!selectMatch) return;

            const optionMatch = Array.from(selectMatch.options).find((option) => {
              if (option.disabled) return false;
              if (optionValueId && option.dataset.optionValueId === optionValueId) return true;
              const optionValueNormalized = normalize(option.value);
              if (value && optionValueNormalized === normalize(value)) return true;
              if (label && option.textContent && normalize(option.textContent) === normalize(label)) return true;
              return false;
            });

            if (optionMatch && selectMatch.value !== optionMatch.value) {
              this.isRestoringSelections = true;
              try {
                selectMatch.value = optionMatch.value;
                Array.from(selectMatch.options).forEach((option) => option.removeAttribute('selected'));
                optionMatch.setAttribute('selected', 'selected');
                selectMatch.dispatchEvent(new Event('change', { bubbles: true }));
              } finally {
                this.isRestoringSelections = false;
              }
            }
          });

          this.capturePersistentOptions();
        });
      }

      getPersistedOptionStore() {
        window.SDG = window.SDG || {};
        window.SDG.Data = window.SDG.Data || {};
        window.SDG.Data.persistedVariantSelections = window.SDG.Data.persistedVariantSelections || {};
        return window.SDG.Data.persistedVariantSelections;
      }

      clearInitialSelection() {
        const $swatches = this.querySelectorAll('.js-product-swatch') || [];

        if (!$swatches.length) {
          this.sendKlaviyoStockEvent();
          return;
        }
        this.querySelectorAll('input[type="radio"][checked]').forEach(el => {
          el.checked = false;
        });
        this.querySelectorAll('select option[selected]').forEach(opt => {
          opt.removeAttribute('selected');
        });

        // disable button
        setTimeout(() => {
          const productForm = this.productForm;
          const waitlistComponent = this.klaviyoWaitlist;
          const canToggleSubmit = productForm && typeof productForm.toggleSubmitButton === 'function';
          const canToggleWaitlist =
            waitlistComponent && typeof waitlistComponent.toggleWaitlistButton === 'function';

          if (!canToggleSubmit) {
            return;
          }

          const waitlistSubmitButton =
            waitlistComponent && waitlistComponent.querySelector('.product-form__submit');
          const isWaitlisted = Boolean(
            canToggleWaitlist && waitlistSubmitButton && waitlistSubmitButton.hasAttribute('waitlist')
          );

          productForm.toggleSubmitButton(true, null, false, isWaitlisted);

          if (isWaitlisted && canToggleWaitlist) {
            waitlistComponent.toggleWaitlistButton(true);
          }
        }, 100); // small timeout for form to initialize
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.restorePersistentOptions();
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination(`KlaviyoProductInfo`);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // Update button state from HTML response (for multi-variant products on variant change)
          this.updateButtonStateFromElement(html.getElementById(`ProductSubmitButton-${this.sectionId}`), html, variant);

          this.sendKlaviyoStockEvent();

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateWaitlistButton(html, $el) {
        const $waitlistEl = html.querySelector('klaviyo-waitlist');

        if (!$el || !$waitlistEl) return;

        // Check if email is currently visible (user is in the middle of waitlist flow)
        const currentEmail = $el.querySelector('.product__klaviyo-email');
        const emailIsVisible = currentEmail && currentEmail.style.display !== 'none';

        // If user is in the middle of the waitlist flow, don't replace HTML
        // This prevents interrupting the user experience
        if (emailIsVisible) {
          return;
        }

        $el.innerHTML = $waitlistEl.innerHTML;
        const $btn = $el.querySelector('.pv__waitlist');
        const $email = $el.querySelector('.product__klaviyo-email');

        // Re-attach event listeners after innerHTML replacement
        if ($btn && typeof $el.onButtonClick === 'function') {
          $btn.addEventListener('click', $el.onButtonClick.bind($el));
        }
        if ($email && typeof $el.onEmailInput === 'function') {
          $email.addEventListener('input', $el.onEmailInput.bind($el));
        }

        // After updating waitlist button HTML, check if we should activate it
        // If a waitlistable size is currently selected, activate the button
        const selectedWaitlistSize = this.querySelector('.js-product-swatch.waitlist:checked');
        if (selectedWaitlistSize && $el && typeof $el.onSizeSelected === 'function') {
          $el.onSizeSelected();
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        const waitlistComponent = this.klaviyoWaitlist;
        if (waitlistComponent && typeof waitlistComponent.toggleWaitlistButton === 'function') {
          waitlistComponent.toggleWaitlistButton(false);
        }

        const productForm = this.productForm;
        if (productForm && typeof productForm.toggleSubmitButton === 'function') {
          productForm.toggleSubmitButton(true, window.variantStrings.unavailable, false, false);
        }

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

        // update mobile Flickity gallery
        const mobileGalleryWrapper = document.querySelector(`.product__media-wrapper-mobile`);
        const newMobileGalleryWrapper = html.querySelector(`.product__media-wrapper-mobile`);
        const mobileGallery = mobileGalleryWrapper?.querySelector('.js-product-gallery');
        const newMobileGallery = newMobileGalleryWrapper?.querySelector('.js-product-gallery');

        if (mobileGallery && newMobileGallery) {
          // Get existing Flickity instance if present
          const flickityInstance = (typeof window.Flickity !== 'undefined' && window.Flickity.data)
            ? window.Flickity.data(mobileGallery)
            : mobileGallery.flickity;

          // Destroy existing instance before updating
          flickityInstance?.destroy();

          // Update gallery HTML
          mobileGallery.innerHTML = newMobileGallery.innerHTML;

          // Update progress bar
          const progressBar = mobileGalleryWrapper?.querySelector('.js-product-gallery-progress-bar');
          const newProgressBar = newMobileGalleryWrapper?.querySelector('.js-product-gallery-progress-bar');
          if (progressBar?.parentElement && newProgressBar?.parentElement) {
            progressBar.parentElement.replaceWith(newProgressBar.parentElement.cloneNode(true));
          }

          // Reinitialize Flickity if needed
          if (flickityInstance) {
            setTimeout(() => {
              const slideCount = mobileGallery.querySelectorAll('.product-media-modal__image-wrapper').length;

              if (slideCount > 1 && typeof window.Flickity !== 'undefined') {
                const newInstance = new window.Flickity(mobileGallery, {
                  cellAlign: 'center',
                  contain: false,
                  prevNextButtons: false,
                  pageDots: false,
                  freeScroll: false,
                  wrapAround: false,
                  adaptiveHeight: true,
                  initialIndex: 0,
                });

                newInstance.select(0, false, true);

                // Update progress bar on slide change
                const progressBar = mobileGalleryWrapper?.querySelector('.js-product-gallery-progress-bar');
                if (progressBar) {
                  const updateProgressBar = () => {
                    const indicatorWidth = 100 / slideCount;
                    const position = (newInstance.selectedIndex / slideCount) * 100;
                    progressBar.style.width = `${indicatorWidth}%`;
                    progressBar.style.transform = `translateX(${position * slideCount}%)`;
                  };

                  updateProgressBar();
                  newInstance.on('change', updateProgressBar);
                }
              } else {
                mobileGallery.classList.remove('hide-items-on-load');
              }
            }, 100);
          }
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      initSizeHoverMessages() {
        const optionLabels = this.querySelectorAll('.js-option-label');
        if (!optionLabels || !optionLabels.length) return;
        const isGiftCard = document.querySelector('.is-gift-card');
        if (isGiftCard) return;

        const showHoverMessage = (hoverMessage, message = '') => {
          if (!hoverMessage) return;
          hoverMessage.style.color = (message === 'Pre-order' || message.startsWith('Restock')) ? '#737373' : '#CA3214';
          hoverMessage.textContent = message;
          hoverMessage.classList.remove('hidden');
        };

        const setPersistentMessage = (hoverMessage, message = '') => {
          if (!hoverMessage) return;

          if (!message) {
            delete hoverMessage.dataset.persistent;
            delete hoverMessage.dataset.persistentMessage;
            hoverMessage.classList.add('hidden');
            return;
          }

          showHoverMessage(hoverMessage, message);
          hoverMessage.dataset.persistent = 'true';
          hoverMessage.dataset.persistentMessage = message;
        };

        const restorePersistentMessage = (hoverMessage) => {
          if (!hoverMessage) return '';

          if (hoverMessage.dataset.persistent === 'true' && hoverMessage.dataset.persistentMessage) {
            showHoverMessage(hoverMessage, hoverMessage.dataset.persistentMessage);
            return;
          }

          hoverMessage.classList.add('hidden');
        };

        const updatePersistentMessage = (formInput) => {
          if (!formInput) return;

          const hoverMessage = formInput.querySelector('.js-swatch-hover-message');
          if (!hoverMessage) return;

          const selectedInput = formInput.querySelector('input[type="radio"]:checked');
          let selectedLabel = null;

          if (selectedInput) {
            selectedLabel = formInput.querySelector(`label[for="${selectedInput.id}"]`);
          }

          if (!selectedLabel) {
            const pressedLabel = formInput.querySelector('.js-option-label[aria-pressed="true"]');
            if (pressedLabel) selectedLabel = pressedLabel;
          }

          if (!selectedLabel) {
            setPersistentMessage(hoverMessage, null);
            return;
          }

          const { available, inventory, preorderProduct, orderableState, shipsLater } = selectedLabel.dataset;

          if (!available || !inventory) {
            setPersistentMessage(hoverMessage, null);
            return;
          }

          const message = this.getAvailabilityMessage(available, inventory, preorderProduct, orderableState, shipsLater);
          setPersistentMessage(hoverMessage, message);
        };

        optionLabels.forEach((label) => {
          if (label.dataset.hoverMessageBound === 'true') return;
          label.dataset.hoverMessageBound = 'true';

          const formInput = label.closest('.js-product-form-input');
          const hoverMessage = formInput?.querySelector('.js-swatch-hover-message');
          if (!hoverMessage) return;

          const { available, inventory, preorderProduct, orderableState, shipsLater } = label.dataset;
          if (!hoverMessage || !available || !inventory) return;

          const showLabelMessage = () => {
            const message = this.getAvailabilityMessage(available, inventory, preorderProduct, orderableState, shipsLater);
            showHoverMessage(hoverMessage, message);
          };

          const persistLabelMessage = () => {
            const message = this.getAvailabilityMessage(available, inventory, preorderProduct, orderableState, shipsLater);
            setPersistentMessage(hoverMessage, message);
          };

          const handleMouseLeave = () => {
            restorePersistentMessage(hoverMessage);
          };

          label.addEventListener('mouseover', showLabelMessage);
          label.addEventListener('focus', showLabelMessage);
          label.addEventListener('mouseout', handleMouseLeave);
          label.addEventListener('blur', handleMouseLeave);

          const inputId = label.getAttribute('for');
          let input = inputId ? document.getElementById(inputId) : null;
          if (!input) {
            input = label.control ?? label.querySelector('input[type="radio"]');
          }

          if (input && input.dataset.hoverMessageChangeBound !== 'true') {
            input.dataset.hoverMessageChangeBound = 'true';
            input.addEventListener('change', () => updatePersistentMessage(formInput));

            if (input.checked) {
              persistLabelMessage();
            }
          }
        });
      }

      getAvailabilityMessage(available, inventory, preorderProduct, orderableState, shipsLater) {
        // Check if waitlistable with restock date
        if (orderableState === 'waitlistable') {
          // If no restock date is set, just show generic message
          if (!shipsLater) {
            return 'Restock soon';
          }

          // If date is set, format it
          try {
            const date = new Date(shipsLater);
            const monthDay = date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            });
            return `Restock soon (${monthDay})`;
          } catch (e) {
            return 'Restock soon';
          }
        }

        if (available === 'false' || inventory === '0') {
          return 'Sold out';
        }

        if (inventory <= 5) {
          return 'Only a few left!';
        }

        if (preorderProduct === 'true') {
          return 'Pre-order';
        }

        return '';
      }

      async sendKlaviyoStockEvent() {
        let klaviyoProductInfo;
        try {
          klaviyoProductInfo = JSON.parse(this.querySelector(`#KlaviyoProductInfo-${this.sectionId}`).textContent);
        } catch (e) {
          console.error('Error getting Klaviyo product info.', e);
          return;
        }

        const {
          item,
          variantStatus,
        } = klaviyoProductInfo;

        if (variantStatus === 'out of stock') {
          await this.createBackInStockSubscription(item);
          return;
        }

        if (variantStatus === 'low stock') {
          klaviyo.track('Low in Stock', item);
        }
      }

      async createBackInStockSubscription(item) {
        try {
          if (!window.klaviyo?.account || !item?.ProductID) {
            return;
          }

          const customerEmail = this.getCustomerEmail();
          const variantCatalogId = `$shopify:::$default:::${item.ProductID}`;
          if (!customerEmail) {
            console.warn('[Klaviyo] Missing customer email for BIS subscription');
            return;
          }

          const profileData = {
            type: 'profile',
            attributes: {
              email: customerEmail,
            },
          };

          const options = {
            method: 'POST',
            headers: {
              accept: 'application/vnd.api+json',
              revision: '2025-07-15',
              'content-type': 'application/vnd.api+json',
            },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: {
                    data: profileData,
                  },
                },
                relationships: {
                  variant: {
                    data: {
                      type: 'catalog-variant',
                      id: variantCatalogId,
                    },
                  },
                },
              },
            }),
          };

          const companyId = await klaviyo.account();
          if (!companyId) {
            return;
          }

          await fetch(
            `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${companyId}`,
            options,
          );
        } catch (error) {
          console.error('[Klaviyo] Failed to create back-in-stock subscription', error);
        }
      }

      getCustomerEmail() {
        const globalEmail = window.SDG?.Data?.customer?.email;
        if (globalEmail) {
          return globalEmail;
        }

        const globalEmailElement = document.querySelector('[data-customer-email]');
        if (globalEmailElement?.dataset?.customerEmail) {
          return globalEmailElement.dataset.customerEmail || null;
        }

        return null;
      }

      /**
       * Updates button state from a button element (either from HTML response or current DOM)
       * This is the source of truth for button state updates - used by both multi-variant and single-variant products
       * @param {HTMLElement} submitButtonElement - The submit button element to read state from
       * @param {Document} html - Optional HTML document (from fetch response), if null uses current DOM
       * @param {Object} variant - Optional variant object
       */
      updateButtonStateFromElement(submitButtonElement, html = null, variant = null) {
        if (!submitButtonElement) return;

        const isWaitlist = submitButtonElement.hasAttribute('waitlist');
        const isDisabled = submitButtonElement.hasAttribute('disabled');
        const shipsLater = submitButtonElement.dataset.shipsLater;

        // Update waitlist button if HTML response provided (for multi-variant products)
        if (html && variant) {
          this.updateWaitlistButton(html, this.klaviyoWaitlist, variant);
        }

        const productForm = this.productForm;
        const waitlistComponent = this.klaviyoWaitlist;
        const canToggleSubmit = productForm && typeof productForm.toggleSubmitButton === 'function';
        const canToggleWaitlist =
          waitlistComponent && typeof waitlistComponent.toggleWaitlistButton === 'function';

        if (canToggleSubmit) {
          productForm.toggleSubmitButton(
            isDisabled ?? true,
            window.variantStrings.soldOut,
            shipsLater,
            Boolean(isWaitlist && isDisabled),
          );
        }

        if (canToggleWaitlist) {
          waitlistComponent.toggleWaitlistButton(isWaitlist);
        }
      }

      /**
       * Initialize button state on page load
       * For single-variant products, this ensures the button state is set correctly since they don't trigger variant changes
       * For multi-variant products, this runs but will be overridden when variants change
       */
      initButtonState() {
        // Check if this is a single-variant product (no selectable variant options)
        const variantSelects = this.variantSelectors;
        let hasSelectableOptions = false;

        if (variantSelects) {
          const hasRadioInputs = variantSelects.querySelectorAll('input[type="radio"].js-product-swatch').length > 0;
          const hasSelectInputs = variantSelects.querySelectorAll('select').length > 0;
          const hasButtonInputs = variantSelects.querySelectorAll('input[type="button"]').length > 0;
          hasSelectableOptions = hasRadioInputs || hasSelectInputs || hasButtonInputs;
        }

        // For multi-variant products, button state will be updated when variants change
        // Only need to initialize for single-variant products
        if (variantSelects && hasSelectableOptions) {
          return;
        }

        // Wait for DOM to be ready, then update button state from current DOM
        setTimeout(() => {
          const productForm = this.productForm;
          if (!productForm) return;

          const submitButton = productForm.querySelector('.product-form__submit');
          if (!submitButton) return;

          // Use the same update logic that handleUpdateProductInfo uses
          this.updateButtonStateFromElement(submitButton);
        }, 100);
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
